import { formatCount, resetDate } from "@/lib/plans/access";

export type LimitRefusal = {
  reason?: string;
  feature?: string;
  limit?: number;
  count?: number;
  used?: number;
  /** When a monthly allowance comes back, as the server sent it. */
  resetsAt?: string;
};

export function refusalFromBody(body: unknown): LimitRefusal | null {
  if (typeof body !== "object" || body === null) return null;
  const detail = "detail" in body && typeof body.detail === "object" && body.detail !== null
    ? body.detail
    : {};
  let reason = "code" in body && typeof body.code === "string"
    ? body.code
    : "reason" in detail && typeof detail.reason === "string"
    ? detail.reason
    : undefined;
  // The scan allowance's refusal. Only a metered Plan gets it: an unlimited one past its fair-use
  // cap slows instead (TRO-2591).
  if (reason === "allowance_exhausted") reason = "scan_limit_reached";
  if (reason !== "limit_reached" && reason !== "scan_limit_reached") return null;
  return {
    reason,
    feature: "feature" in detail && typeof detail.feature === "string" ? detail.feature : undefined,
    limit: "limit" in detail && typeof detail.limit === "number" ? detail.limit : undefined,
    count: "count" in detail && typeof detail.count === "number" ? detail.count : undefined,
    used: "used" in detail && typeof detail.used === "number" ? detail.used : undefined,
    resetsAt: "resets_at" in detail && typeof detail.resets_at === "string"
      ? detail.resets_at
      : undefined,
  };
}

/** What a person can still do for free; never a move into the Personal Collection (ADR-0169). */
export type FreePath = { note?: string; action: string; route: "/stock-groups" | "/inventory"; };

export type LimitPrompt = {
  title: string;
  /** The line under the title: when the allowance comes back, or the free path's note. */
  note?: string;
  feature: string;
  upgrade: boolean;
  csv: boolean;
  /** Offers typing a card name, which never uses a scan. */
  typed: boolean;
  free?: FreePath;
};

/** The design's number-first headline per counted limit, and the limit's name when no count came. */
const HEADLINES: Record<
  string,
  { counted: (used: number, limit: number) => string; name: string; }
> = {
  locations: {
    counted: (used, limit) => `${formatCount(used)} of ${formatCount(limit)} Locations used`,
    name: "Locations",
  },
  inventory_on_hand: {
    counted: (used, limit) => `${formatCount(used)} of ${formatCount(limit)} cards on hand`,
    name: "inventory",
  },
  want_list: {
    counted: (used, limit) => `${formatCount(used)} of ${formatCount(limit)} Wants used`,
    name: "Want list",
  },
  buylist_card_entries: {
    counted: (used, limit) => `${formatCount(used)} of ${formatCount(limit)} buy list cards used`,
    name: "buy list",
  },
  // Shown only when the refusal has no reset date to name the month by.
  scans_per_month: {
    counted: (used, limit) => `You've used ${formatCount(used)} of ${formatCount(limit)} scans`,
    name: "scans",
  },
};

const FREE_PATHS: Record<string, FreePath> = {
  locations: { action: "Delete or merge a Location", route: "/stock-groups" },
  inventory_on_hand: {
    note: "Delete cards or keep selling until you're under.",
    action: "Delete or sell cards",
    route: "/inventory",
  },
};

/** The server's refusal owns the numbers; unknown refusals stay generic. */
export function limitPrompt(refusal: LimitRefusal): LimitPrompt {
  const feature = refusal.reason === "want_limit_reached" ? "want_list" : refusal.feature;
  const headline = feature && HEADLINES[feature];
  if (
    !headline
    || !["limit_reached", "want_limit_reached", "scan_limit_reached"].includes(refusal.reason ?? "")
  ) {
    return {
      title: "You've reached a limit on your Plan",
      feature: feature ?? "",
      upgrade: true,
      csv: false,
      typed: false,
    };
  }
  const used = refusal.count ?? refusal.used;
  const title = typeof used === "number" && typeof refusal.limit === "number"
    ? headline.counted(used, refusal.limit)
    : `You've reached your ${headline.name} limit`;
  if (feature === "scans_per_month") {
    const resetsAt = refusal.resetsAt;
    return {
      title: resetsAt && typeof refusal.limit === "number"
        ? `You've used all ${formatCount(refusal.limit)} scans for ${periodMonth(resetsAt)}`
        : title,
      note: `${
        resetsAt ? `They come back ${resetDate(resetsAt)}. ` : ""
      }Typing a card name is always free.`,
      feature,
      upgrade: true,
      csv: true,
      typed: true,
    };
  }
  const free = FREE_PATHS[feature];
  return { title, note: free?.note, feature, upgrade: true, csv: false, typed: false, free };
}

/** The month a period that resets at `resetsAt` (00:00 UTC on the 1st) counted, "October". */
function periodMonth(resetsAt: string): string {
  return new Date(new Date(resetsAt).getTime() - 1).toLocaleDateString(undefined, {
    month: "long",
    timeZone: "UTC",
  });
}

let listener: ((refusal: LimitRefusal) => void) | null = null;

export function setLimitPromptListener(next: ((refusal: LimitRefusal) => void) | null): void {
  listener = next;
}

export function showLimitPrompt(refusal: LimitRefusal): void {
  listener?.(refusal);
}

let typeInstead: (() => void) | null = null;

/**
 * A mounted scanner offers its own card search for "Type a card name instead". Returns the
 * unregister, which leaves a newer scanner's handler in place.
 */
export function setTypeInsteadHandler(next: () => void): () => void {
  typeInstead = next;
  return () => {
    if (typeInstead === next) typeInstead = null;
  };
}

/** The mounted scanner's search, or `null` when no scanner offered one. */
export function typeInsteadHandler(): (() => void) | null {
  return typeInstead;
}
